import { z } from 'zod';
import { getCurrency, type CurrencyCode } from './currency.js';

/**
 * The money value object.
 *
 * Money arrives from providers as a decimal `number` plus a currency string
 * and is stored as `float8`, which makes raw arithmetic on those numbers
 * lossy in ways that matter for accounting: `0.1 + 0.2 !== 0.3`, a
 * cumulative→delta subtraction leaves ULP dust that reads as a nonzero
 * charge, and an amount tagged with some other currency looks exactly like
 * dollars. {@link Money} closes all three: every amount is an opaque integer
 * count of nanounits (1e-9 of the major unit, the granularity below which no
 * provider bills) tagged with its ISO 4217 currency code, it can only be
 * built through a validating constructor, and TypeScript rejects `a - b` on
 * it so the only way to combine two amounts is the pure helpers below — each
 * of which returns a new object and refuses to mix currencies, at compile
 * time when the codes are known literally and with
 * {@link CurrencyMismatchError} at runtime otherwise.
 *
 * Reflex records and computes in USD only ({@link UsdAmount} and the `usd*`
 * helpers); other currencies exist so a boundary can recognize and reject
 * them, not so application code can hold them. Callers convert back to a
 * decimal `number` with {@link moneyToDecimal} / {@link usdToDollars} at the
 * wire/DB boundary — and nowhere else. Policy and rationale:
 * `docs/internal/architecture/money.md`.
 *
 * @module @reflex/shared/money
 */

const NANOS_PER_UNIT = 1_000_000_000;

declare const moneyBrand: unique symbol;

/**
 * An exact, non-arithmetic amount of one currency. Build it with
 * {@link moneyFromDecimal} (or {@link usdFromDollars} for the USD case);
 * combine amounts only through the pure helpers in this module.
 */
export interface Money<C extends CurrencyCode = CurrencyCode> {
  /** Integer nanounits (1e-9 of the major unit). */
  readonly nanos: bigint;
  /** ISO 4217 alphabetic code, e.g. `'USD'`. */
  readonly currencyCode: C;
  readonly [moneyBrand]: true;
}

/** The USD specialization of {@link Money} — the only currency the backend records. */
export type UsdAmount = Money<'USD'>;

/** ISO 4217 currency code used for every amount Reflex records. */
export const USD_CURRENCY = 'USD' as const;

/**
 * A currency-tagged amount as it appears on the wire: the shape of the ACP
 * `Cost` object, and the same shape every other provider reports money in.
 * Parsing it into a {@link Money} is the only supported way in.
 */
export const MonetaryAmountSchema = z.object({
  amount: z.number(),
  currency: z.string(),
});
export type MonetaryAmount = z.infer<typeof MonetaryAmountSchema>;

/** Thrown when an arithmetic helper is handed two different currencies. */
export class CurrencyMismatchError extends Error {
  constructor(operation: string, a: CurrencyCode, b: CurrencyCode) {
    super(`cannot ${operation} ${a} and ${b}: money arithmetic requires one currency`);
    this.name = 'CurrencyMismatchError';
  }
}

function fromNanos<C extends CurrencyCode>(nanos: bigint, currencyCode: C): Money<C> {
  return { nanos, currencyCode } as Money<C>;
}

/** Quantize the shortest decimal representation of a finite number without overflowing. */
function decimalToNanos(amount: number): bigint {
  const negative = amount < 0;
  const [coefficient, exponentText] = Math.abs(amount).toString().split('e');
  const [whole = '', fraction = ''] = coefficient?.split('.') ?? [];
  const digits = BigInt(`${whole}${fraction}`);
  const exponent = Number(exponentText ?? 0) - fraction.length + 9;

  if (exponent >= 0) {
    const nanos = digits * 10n ** BigInt(exponent);
    return negative ? -nanos : nanos;
  }

  const divisor = 10n ** BigInt(-exponent);
  let rounded = digits / divisor;
  const remainder = digits % divisor;
  // Match Math.round: positive ties round up; negative ties round toward +Infinity.
  if (remainder * 2n > divisor || (!negative && remainder * 2n === divisor)) rounded += 1n;
  return negative ? -rounded : rounded;
}

/**
 * Mixing currencies inside arithmetic is a programming error — the backend
 * holds USD only, and a future FX layer will translate at the boundary, not
 * mid-sum — so it throws rather than returning a nullable result every
 * call site would have to unwrap.
 */
function assertSameCurrency(operation: string, a: Money, b: Money): void {
  if (a.currencyCode !== b.currencyCode) {
    throw new CurrencyMismatchError(operation, a.currencyCode, b.currencyCode);
  }
}

/**
 * Registered, canonically-cased codes only: `'usd'` tagged as-is would mint
 * an amount every USD helper rejects (`isUsd` false, `addMoney` with real
 * USD throws), and normalizing silently would let the value's runtime tag
 * diverge from its literal type `C`.
 */
function isCanonicalCurrencyCode(code: CurrencyCode): boolean {
  return getCurrency(code)?.currencyCode === code;
}

/**
 * Zero of the given currency. Throws on a code outside the shared registry
 * (or not in its canonical uppercase form) — zero is still a tagged amount,
 * and an unregistered tag poisons every operation downstream.
 */
export function zeroMoney<C extends CurrencyCode>(currencyCode: C): Money<C> {
  if (!isCanonicalCurrencyCode(currencyCode)) {
    throw new TypeError(`unknown currency code: ${currencyCode}`);
  }
  return fromNanos(0n, currencyCode);
}

/** Zero dollars. */
export const ZERO_USD: UsdAmount = zeroMoney(USD_CURRENCY);

/**
 * Build an amount from a decimal number of major units, quantizing to the
 * nearest nanounit. Returns `null` for a currency code not in the shared
 * registry (or not in its canonical uppercase form — see
 * {@link isCanonicalCurrencyCode}) or a non-finite amount. The bigint-backed
 * representation has no fixed magnitude cap; precision is still limited by
 * the decimal `number` supplied at this boundary.
 */
export function moneyFromDecimal<C extends CurrencyCode>(
  amount: number,
  currencyCode: C,
): Money<C> | null {
  if (!isCanonicalCurrencyCode(currencyCode)) return null;
  if (!Number.isFinite(amount)) return null;
  return fromNanos(decimalToNanos(amount), currencyCode);
}

/** Build a USD amount from decimal dollars. See {@link moneyFromDecimal}. */
export function usdFromDollars(dollars: number): UsdAmount | null {
  return moneyFromDecimal(dollars, USD_CURRENCY);
}

/**
 * Parse a currency-tagged wire amount into a {@link Money} of whatever
 * registered currency it names. Returns `null` when the value is not a
 * {@link MonetaryAmount}, names a currency outside the shared registry, or
 * cannot be held exactly. The currency comparison is case-insensitive.
 */
export function moneyFromMonetaryAmount(value: unknown): Money | null {
  const parsed = MonetaryAmountSchema.safeParse(value);
  if (!parsed.success) return null;
  const currency = getCurrency(parsed.data.currency);
  if (!currency) return null;
  return moneyFromDecimal(parsed.data.amount, currency.currencyCode);
}

/**
 * Parse a currency-tagged wire amount that must be USD. Returns `null` when
 * the value is not a {@link MonetaryAmount}, is denominated in anything but
 * USD (a euro amount recorded as dollars is a silent mispricing), or cannot
 * be held exactly. The currency comparison is case-insensitive.
 */
export function usdFromMonetaryAmount(value: unknown): UsdAmount | null {
  const parsed = moneyFromMonetaryAmount(value);
  return parsed && isUsd(parsed) ? parsed : null;
}

/** Narrow an amount of unknown currency to USD. */
export function isUsd(amount: Money): amount is UsdAmount {
  return amount.currencyCode === USD_CURRENCY;
}

/** Convert back to a decimal number of major units for the wire or the database. */
export function moneyToDecimal(amount: Money): number {
  const whole = amount.nanos / 1_000_000_000n;
  const fraction = amount.nanos % 1_000_000_000n;
  return Number(whole) + Number(fraction) / NANOS_PER_UNIT;
}

/** Convert back to decimal dollars for the wire or the database. */
export function usdToDollars(amount: UsdAmount): number {
  return moneyToDecimal(amount);
}

/**
 * Exact sum `a + b` of one currency, as a new object.
 *
 * `NoInfer` pins `C` to `a`'s currency, so handing `b` a different literal
 * currency type is a compile error rather than only a runtime
 * {@link CurrencyMismatchError} (the same holds for every two-amount helper
 * below). The bigint representation makes addition exact at any magnitude.
 */
export function addMoney<C extends CurrencyCode>(a: Money<C>, b: Money<NoInfer<C>>): Money<C> {
  assertSameCurrency('add', a, b);
  return fromNanos(a.nanos + b.nanos, a.currencyCode);
}

/** Exact difference `a - b` of one currency, as a new object; may be negative. */
export function subtractMoney<C extends CurrencyCode>(a: Money<C>, b: Money<NoInfer<C>>): Money<C> {
  assertSameCurrency('subtract', a, b);
  return fromNanos(a.nanos - b.nanos, a.currencyCode);
}

/**
 * Scale an amount by a dimensionless factor (a token count over a million,
 * a percentage, `1 / records`), quantized to the nearest nanounit. Returns
 * `null` when the factor or resulting decimal amount is non-finite.
 */
export function scaleMoney<C extends CurrencyCode>(
  amount: Money<C>,
  factor: number,
): Money<C> | null {
  if (!Number.isFinite(factor)) return null;
  return moneyFromDecimal(moneyToDecimal(amount) * factor, amount.currencyCode);
}

/** The larger of two amounts of one currency — the clamp for a difference that must not go below zero. */
export function maxMoney<C extends CurrencyCode>(a: Money<C>, b: Money<NoInfer<C>>): Money<C> {
  assertSameCurrency('compare', a, b);
  return a.nanos >= b.nanos ? a : b;
}

/** The smaller of two amounts of one currency. */
export function minMoney<C extends CurrencyCode>(a: Money<C>, b: Money<NoInfer<C>>): Money<C> {
  assertSameCurrency('compare', a, b);
  return a.nanos <= b.nanos ? a : b;
}

/** Negative when `a < b`, zero when equal, positive when `a > b`. One currency only. */
export function compareMoney<C extends CurrencyCode>(a: Money<C>, b: Money<NoInfer<C>>): number {
  assertSameCurrency('compare', a, b);
  return a.nanos < b.nanos ? -1 : a.nanos > b.nanos ? 1 : 0;
}

/** True when the amounts are the same currency and the same number of nanounits. */
export function moneyEquals(a: Money, b: Money): boolean {
  return a.currencyCode === b.currencyCode && a.nanos === b.nanos;
}

/** True for any amount above zero — i.e. something was actually billed. */
export function isPositiveMoney(amount: Money): boolean {
  return amount.nanos > 0n;
}

export function isNegativeMoney(amount: Money): boolean {
  return amount.nanos < 0n;
}

export function isZeroMoney(amount: Money): boolean {
  return amount.nanos === 0n;
}

/** Exact sum of any number of USD amounts. */
export function sumUsd(amounts: Iterable<UsdAmount>): UsdAmount {
  let total = ZERO_USD;
  for (const amount of amounts) total = addMoney(total, amount);
  return total;
}

/** Non-finite inputs count as $0 so one bad row cannot NaN a whole rollup. */
function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * Binary operation on decimal dollars, quantized to nanodollars and combined
 * exactly with the same bigint representation as {@link Money}.
 */
function dollarOp(a: number, b: number, combine: (an: bigint, bn: bigint) => bigint): number {
  a = finiteOrZero(a);
  b = finiteOrZero(b);
  const ma = usdFromDollars(a);
  const mb = usdFromDollars(b);
  if (!ma || !mb) return a + b;
  return moneyToDecimal(fromNanos(combine(ma.nanos, mb.nanos), USD_CURRENCY));
}

/**
 * Boundary conveniences for code that holds decimal-dollar `number`s it
 * cannot re-type — SQL SUM results, wire payloads feeding a chart — but
 * still needs sound arithmetic on them. Each routes through {@link Money}
 * (quantize → exact integer math → convert back), so `0.1 + 0.2` is `0.3`,
 * not `0.30000000000000004`. A non-finite input counts as $0 rather than
 * poisoning the aggregate with `NaN`. Application code that can hold a
 * {@link UsdAmount} should; these are for the edges.
 */
export function addDollars(a: number, b: number): number {
  return dollarOp(a, b, (an, bn) => an + bn);
}

/** `a - b` on decimal dollars. See {@link addDollars} for the exactness contract. */
export function subtractDollars(a: number, b: number): number {
  return dollarOp(a, -b, (an, bn) => an + bn);
}

/** Sum of decimal dollars. See {@link addDollars} for the exactness contract. */
export function sumDollars(values: Iterable<number>): number {
  let total = 0;
  for (const value of values) total = addDollars(total, value);
  return total;
}

/** Exact sum `a + b`. See {@link addMoney}. */
export const addUsd: (a: UsdAmount, b: UsdAmount) => UsdAmount = addMoney;

/** Exact difference `a - b`; may be negative. See {@link subtractMoney}. */
export const subtractUsd: (a: UsdAmount, b: UsdAmount) => UsdAmount = subtractMoney;

/** The larger of two amounts — the clamp for a difference that must not go below zero. */
export const maxUsd: (a: UsdAmount, b: UsdAmount) => UsdAmount = maxMoney;

/** Negative when `a < b`, zero when equal, positive when `a > b`. */
export const compareUsd: (a: UsdAmount, b: UsdAmount) => number = compareMoney;

/** True for any amount above zero — i.e. something was actually billed. */
export const isPositiveUsd: (amount: UsdAmount) => boolean = isPositiveMoney;

export const isNegativeUsd: (amount: UsdAmount) => boolean = isNegativeMoney;
